/* eslint-disable max-lines */
import { useLocation } from 'react-router-dom';
import React,{
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import RxOp from 'rxjs-operators';
import Card from '@material-ui/core/Card';
import Box from '@material-ui/core/Box';
import CardContent from '@material-ui/core/CardContent';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import LinearProgress from '@material-ui/core/LinearProgress';
import Toolbar from 'components/Layout/Toolbar';

import {
  ICriticalityMetrics,
  IHistoryMetrics,
  IScoreMetrics,
  IVentureStatusMetrics,
  statusesDashboard,
} from 'interfaces/dashboard';
import { statuses } from 'interfaces/file';
import dashboardService from 'services/dashboard';
import selectedVentureService from 'services/profile';
import ventureService from 'services/venture';
import CardGraphs from '../Component/Card';
import DocsActives from '../Graphs/Actives';
import DocumentCriticality from '../Graphs/Criticality';
import HistoricStatus from '../Graphs/HistoricStatus';
import ScoreVenture from '../Graphs/Score';
import DocumentStatus from '../Graphs/Status';
import EntityStatus from '../Graphs/Statuses';
import SnapshotPicker from '../Component/SnapshotPicker';
import DialogStatusByDay from '../Component/DialogStatusByDay';
import { useSnapshots } from '../context/SnapshotsContext';

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 700,
  },
  wrapperGrid: {
    [theme.breakpoints.up('md')]: {
      flexWrap: 'nowrap',
    },
    backgroundColor: '#8b7c3f',
    borderRadius: '16px',
  },
}));

const ViewVenture = () => {
  const { search } = useLocation();
  const containerToPrint = useRef();
  const { getUrlWithSnap, redirectWithSnap } = useSnapshots();

  const [ventureMetrics, setVentureMetrics] = useState<IVentureStatusMetrics>();
  const [critacalityMetrics, setCritacalityMetrics] =
    useState<ICriticalityMetrics[]>();
  const [scoreMetrics, setScoreMetrics] = useState<IScoreMetrics>();
  const [historyMetrics, setHistoryMetrics] = useState<IHistoryMetrics>();
  const [ventureTitle, setVentureTitle] = useState<string>();
  const [idVenture, setIdVenture] = useState<number>();
  const [isOpenDialogStatusByDay, setIsOpenDialogStatusByDay] =
    useState<boolean>(false);
  const [currentStatusByDay, setCurrentStatusByDay] =
    useState<statusesDashboard>();
  const classes = useStyles();
  const [date, setDate] = useState<Date | null>(null);

  const handleChangeDate = (date: Date | null) => setDate(date);

  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  useEffect(() => {
    selectedVentureService
      .getSelectedVenture()
      .pipe(RxOp.logError())
      .subscribe((selectedVenture) => {
        if (selectedVenture) {
          setIdVenture(selectedVenture.id);
          setVentureTitle(selectedVenture.name);

          ventureService
            .getById(selectedVenture.id)
            .pipe(RxOp.logError())
            .subscribe((venture) => {
              dashboardService
                .getStatus({ ventureId: venture.id, params: queryParams })
                .pipe(RxOp.logError())
                .subscribe((m) => setVentureMetrics(m));

              dashboardService
                .getCriticality({ ventureId: venture.id, params: queryParams })
                .pipe(RxOp.logError())
                .subscribe((m) => setCritacalityMetrics(m));

              dashboardService
                .getScore({ ventureId: venture.id, params: queryParams })
                .pipe(RxOp.logError())
                .subscribe((m) => setScoreMetrics(m));

              dashboardService
                .getHistory({ ventureId: venture.id, params: queryParams })
                .pipe(RxOp.logError())
                .subscribe((m) => setHistoryMetrics(m));
            });
        }
      });
  }, [queryParams]);

  const { statusByVenture = null, statusByCategory = null } =
    ventureMetrics || {};
  const totalDocumentsVenture = statusByVenture
    ? statusByVenture.find(
        (i) => i.status === statusesDashboard.ACTIVE_DOCUMENTS
      ).value
    : 0;

  const handleDashboardEntity = useCallback(
    (categoryId: number) => () => {
      redirectWithSnap(
        `/documentation/dashboard/venture/${
          idVenture || 0
        }/category/${categoryId}`
      );
    },
    [idVenture, redirectWithSnap]
  );

  const findStatus = (status: statusesDashboard) => {
    return statusByVenture.find((i) => status === i.status);
  };

  const getTotalByCategory = useCallback(
    (categoryId: number): number =>
      (statusByCategory && statusByCategory)
        .find((item) => item.categoryId === categoryId)
        .data.find((item) => item.status === statusesDashboard.ACTIVE_DOCUMENTS)
        .value || 0,
    [statusByCategory]
  );

  const handleCloseStatusByDay = () => {
    setIsOpenDialogStatusByDay(false);
    setCurrentStatusByDay(undefined);
  };

  const handleOpenStatusByDay = (status: statusesDashboard) => {
    setIsOpenDialogStatusByDay(true);
    setCurrentStatusByDay(status);
  };

  return (
    <>
      <Toolbar
        title="Dashboard"
        containerToPrint={containerToPrint}
        breadcrumbs={[
          {
            title: 'Dashboard',
            href: getUrlWithSnap('/documentation/dashboard'),
          },
          {
            title: ventureTitle,
            href: getUrlWithSnap(
              `/documentation/dashboard/venture/${idVenture}`
            ),
          },
        ]}
      />
      <DialogStatusByDay
        open={isOpenDialogStatusByDay}
        onClose={handleCloseStatusByDay}
        status={currentStatusByDay}
        date={date}
        ventureId={idVenture}
      />
      <Container innerRef={containerToPrint} style={{ marginTop: '40px' }}>
        <Card>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingLeft: 20,
              paddingRight: 20,
            }}
          >
            <Typography
              variant="h6"
              color="primary"
              style={{ fontWeight: 700 }}
            >
              {ventureTitle}
            </Typography>

            <div>
              <SnapshotPicker onChangeDate={handleChangeDate} />
            </div>
          </Box>

          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {!Boolean(
                  ventureMetrics &&
                    critacalityMetrics &&
                    scoreMetrics &&
                    historyMetrics
                ) && <LinearProgress color="secondary" />}
              </Grid>
              <Grid item xs={12}>
                <Grid container className={classes.wrapperGrid}>
                  {statusByCategory &&
                    statusByCategory.map((category) => (
                      <Grid
                        item
                        key={`entity-status-graph-${category.categoryId}`}
                        xs={12}
                        sm={6}
                      >
                        <EntityStatus
                          entityName={category.categoryName}
                          metrics={category.data.filter((i) =>
                            [
                              ...Object.keys(statuses),
                              statusesDashboard.NOT_UPLOADED,
                            ].includes(i.status)
                          )}
                          total={getTotalByCategory(category.categoryId)}
                          onClick={handleDashboardEntity(category.categoryId)}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Grid>
              <Grid item xs={12} lg={9} xl={10}>
                {statusByVenture && (
                  <CardGraphs bgcolor="#161d42">
                    <Grid container>
                      {statusByVenture
                        .filter(
                          (item) =>
                            item.status !== statusesDashboard.ACTIVE_DOCUMENTS
                        )
                        .map((ds) => (
                          <Grid
                            item
                            key={`document-status-graph-${ds.status}`}
                            xs={12}
                            sm={6}
                            lg={3}
                          >
                            <DocumentStatus
                              metrics={ds}
                              total={totalDocumentsVenture}
                              onClick={() => handleOpenStatusByDay(ds.status)}
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </CardGraphs>
                )}
              </Grid>
              <Grid item xs={12} lg={3} xl={2}>
                <Grid container>
                  {statusByVenture &&
                    findStatus(statusesDashboard.ACTIVE_DOCUMENTS) && (
                      <Grid item xs={12} sm={6} lg={12}>
                        <DocsActives
                          metrics={findStatus(
                            statusesDashboard.ACTIVE_DOCUMENTS
                          )}
                        />
                      </Grid>
                    )}
                  <Grid item xs={12} sm={6} lg={12}>
                    <DocumentCriticality metrics={critacalityMetrics || []} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} lg={9} xl={10}>
                    <HistoricStatus
                      metrics={
                        historyMetrics ? historyMetrics.statusByMonth : []
                      }
                    />
                  </Grid>
                  <Grid item xs={12} lg={3} xl={2}>
                    <ScoreVenture
                      metrics={scoreMetrics || { max: 0, score: 0 }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};
export default ViewVenture;
